// itemDetailed/ItemDetailed.tsx
import React from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  MdFavorite,
  MdFavoriteBorder,
  MdVisibility,
  MdStar,
  MdCategory,
} from "react-icons/md";
import { SaleItemInterface } from "../../../../../common/types/types";
import { SaleItemAttributes } from "../../../../../common/components/saleItemAttributes/SaleItemAttributes";
import { useItem } from "../useItem";
import { ItemVariant } from "../../../../../common/types/enums";
import { useTheme } from "../../../../../common/hooks/useTheme";
import { useCartActions } from "../../../../../common/hooks/useCartActions";
import styles from "./ItemDetailed.module.scss";
import { getImageUri } from "../../../../../common/utils/functions/getImageUri";
import { StockBadge } from "../../../../../common/components/stockBage/StockBage";
import { formatTimeAgo } from "../../../../../common/utils/functions/formatTimeAgo";
import { shortenText } from "../../../../../common/utils/functions/shortenText";
import { ROUTES } from "../../../../../common/utils/constants";
import { selectCurrentCluster } from "../../../../../store/slices/currentClusterSlice";
import { selectForceStores } from "../../../../../store/slices/storeListSlice";
import { ItemCardButton } from "../../../../../common/components/itemCartButton/ItemCardButton";
import { formatPriceToLocale } from "../../../../../common/utils/functions/formatPriceToLocale";
import { useDisplayPrice } from "../../../../../common/hooks/useDisplayPrice";
import { RootState } from "../../../../../store/store";

type Props = {
  item: SaleItemInterface;
  getCategoryName: (id: number) => string | undefined;
  variant?: ItemVariant;
  horizontal?: boolean;
  isAuth?: boolean;
  usingStock?: boolean;
};

export const ItemDetailed = ({
  item,
  getCategoryName,
  variant = ItemVariant.DETAILED,
  horizontal = false,
  isAuth,
  usingStock,
}: Props) => {
  const { theme } = useTheme();
  const currentCluster = useSelector(selectCurrentCluster);
  const forceStore = currentCluster?.forceStore ?? false;
  const selectedStoreIds = useSelector(selectForceStores);
  const getPrice = useDisplayPrice();

  const {
    isFavorite,
    renderData,
    handleFavoritePress,
    currentUser,
    updatedItem,
    isOutOfStock,
    isIncreaseDisabled,
  } = useItem(item);

  const {
    quantity,
    isComplexCategory,
    showComplexButton,
    showCartButton,
    handleAddToCart,
    handleAddToComplex,
    handleIncrease,
    handleDecrease,
    handleRemove,
  } = useCartActions(item);

  const isInComplexCart = useSelector((state: RootState) =>
    state.complexOrderCart.items.some((i) => i.itemId === item.id),
  );

  const isMinimal = variant === ItemVariant.MINIMAL;

  const showStatsOnImage = renderData.showFavorites || renderData.showViews;
  const displayPrice = getPrice(item.prices);
  const hasPrice = Boolean(displayPrice);

  const firstImage =
    item.files?.find(
      (path) => !path.includes("video_") && !path.includes("poster_"),
    ) ||
    item.files?.find((path) => path.includes("poster_")) ||
    null;

  if (!item) return null;

  return (
    <Link
      to={`${ROUTES.ITEM}/${item.id}`}
      className={`${styles.container} ${horizontal ? styles.horizontal : ""} ${isMinimal ? styles.minimalMode : ""}`}
    >
      <div className={styles.imageContainer}>
        {firstImage && (
          <img
            className={styles.image}
            src={`${getImageUri()}${firstImage}`}
            alt={item.name}
          />
        )}

        {currentUser && (
          <button
            className={styles.favoriteButton}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFavoritePress();
            }}
            aria-label={
              isFavorite ? "Удалить из избранного" : "Добавить в избранное"
            }
          >
            {isFavorite ? (
              <MdFavorite size={16} color="#fff" />
            ) : (
              <MdFavoriteBorder size={16} color="#fff" />
            )}
          </button>
        )}

        {showStatsOnImage && (
          <div className={styles.statsOverlay}>
            {renderData.showFavorites && (
              <div className={styles.overlayStatItem}>
                <MdFavoriteBorder size={12} color="#fff" />
                <span className={styles.overlayStatText}>
                  {Number(renderData.totalFavorites)}
                </span>
              </div>
            )}
            {renderData.showViews && (
              <div className={styles.overlayStatItem}>
                <MdVisibility size={12} color="#fff" />
                <span className={styles.overlayStatText}>
                  {Number(renderData.totalViews)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.mainContent}>
          <div className={styles.nameContainer}>
            <span className={styles.nameText} title={item.name}>
              {item.name}
            </span>
          </div>

          <div className={styles.priceStockContainer}>
            <div className={styles.priceContainer}>
              <span className={styles.priceText}>
                {hasPrice
                  ? `${formatPriceToLocale(displayPrice)} ₽`
                  : showComplexButton
                    ? ""
                    : "Нет цены"}
              </span>
            </div>

            {usingStock && (
              <div className={styles.stockBadgeContainer}>
                <StockBadge
                  stockStores={updatedItem.stock_stores}
                  stockEnabled={updatedItem.useStock}
                  forceStore={forceStore}
                  itemStops={updatedItem.item_stops}
                  selectedStoreIds={selectedStoreIds}
                />
              </div>
            )}
          </div>

          <div
            className={`${styles.infoRow} ${isMinimal ? styles.hideOnMinimal : ""}`}
          >
            {isAuth && renderData.lastView && (
              <span className={styles.lastViewedText}>
                Вы смотрели{" "}
                {formatTimeAgo(renderData.lastView.viewedAt as string)}
              </span>
            )}

            {renderData.showRating && (
              <div className={styles.ratingContainer}>
                <MdStar size={12} color="#FFD700" />
                <span className={styles.ratingText}>
                  {renderData.averageRating!.toFixed(1)}
                </span>
                {renderData.totalRatings! > 0 && (
                  <span className={styles.ratingCount}>
                    ({renderData.totalRatings})
                  </span>
                )}
              </div>
            )}
          </div>

          <span
            className={`${styles.description} ${isMinimal ? styles.hideOnMinimal : ""}`}
          >
            {item.description}
          </span>

          <div
            className={`${styles.attrSection} ${isMinimal ? styles.hideOnMinimal : ""}`}
          >
            {item.attributes && item.attributes.length > 0 ? (
              <SaleItemAttributes attributes={item.attributes} small />
            ) : (
              <div className={styles.attrPlaceholder} />
            )}
          </div>

          <div className={styles.categoryContainer}>
            <MdCategory size={14} color={theme.textSecondary} />
            <span className={styles.category}>
              {shortenText(getCategoryName(Number(item.categoryId)), 15)}
            </span>
          </div>
        </div>

        <div className={styles.buttonContainer}>
          <ItemCardButton
            quantity={quantity}
            showCartButton={showCartButton}
            showComplexButton={showComplexButton}
            isComplexCategory={isComplexCategory}
            isOutOfStock={isOutOfStock}
            isIncreaseDisabled={isIncreaseDisabled}
            hasPrice={hasPrice}
            isInComplexCart={isInComplexCart}
            onAddToCart={handleAddToCart}
            onAddToComplex={handleAddToComplex}
            onIncrease={handleIncrease}
            onDecrease={handleDecrease}
            onRemove={handleRemove}
          />
        </div>
      </div>
    </Link>
  );
};
